import { useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { ICreateInternalOrganisationRequest } from 'shared';

import { Modal } from '../../common/Atoms/Modal';
import { Button } from '../../common/Atoms/Button';
import { ComboBox } from '../../common/Atoms/ComboBox';
import { InputTextAddon } from '../../common/Atoms/InputTextAddon';
import { Paragraph } from '../../common/Atoms/Typography/Paragraph';
import { useTimezoneList } from '../hooks/useTimezoneList';
import { useCreateInternalOrganisationMutation } from '../services/api/organisationApi/organisation';

interface CreateInternalOrganisationModalProps {
  open: boolean;
  onClose: () => void;
}

/** Development only: create an organisation with no Xero or QuickBooks behind it */
export function CreateInternalOrganisationModal({ open, onClose }: CreateInternalOrganisationModalProps) {
  const navigate = useNavigate();
  const [createInternalOrganisation, { isLoading }] = useCreateInternalOrganisationMutation();
  const [tzSearch, setTzSearch] = useState(``);
  const { allOptions, filteredOptions } = useTimezoneList(tzSearch);
  const [form, setForm] = useState<ICreateInternalOrganisationRequest>({
    legalName: ``,
    region: ``,
    baseCurrency: ``,
    timezone: ``,
  });

  function onChange(e: { name: string; value: string | number }) {
    setForm(prev => ({ ...prev, [e.name]: String(e.value) }));
  }

  async function onSubmit() {
    try {
      const { id } = await createInternalOrganisation(form).unwrap();

      onClose();
      navigate(`/${id}`);
    }
    catch (err) {
      toast.error(err?.data?.message || `Could not create organisation`);
    }
  }

  return (
    <Modal
      title={ `Create Internal organisation` }
      open={ open }
      setOpen={ onClose }
    >
      <div className={ `flex flex-col gap-4 mt-4` }>
        <Paragraph variant={ `secondary` }>
          { `Development only. Internal organisations have no accounting system connected, all data lives in Paidnice.` }
        </Paragraph>
        <InputTextAddon
          label={ `Legal name` }
          name={ `legalName` }
          value={ form.legalName }
          onChange={ onChange }
        />
        <InputTextAddon
          label={ `Region` }
          name={ `region` }
          description={ `2 letter country code, e.g. AU` }
          maxLength={ 2 }
          value={ form.region }
          onChange={ onChange }
        />
        <InputTextAddon
          label={ `Base currency` }
          name={ `baseCurrency` }
          description={ `3 letter currency code, e.g. AUD` }
          maxLength={ 3 }
          value={ form.baseCurrency }
          onChange={ onChange }
        />
        <ComboBox
          label={ `Timezone` }
          options={ filteredOptions }
          selected={ allOptions.find(tz => tz.value === form.timezone) || null }
          onSelect={ ({ value }) => onChange({ name: `timezone`, value }) }
          query={ tzSearch }
          onQueryChange={ e => setTzSearch(e.target.value) }
        />
        <div className={ `flex justify-end` }>
          <Button
            onClick={ onSubmit }
            loading={ isLoading }
            disabled={ !form.legalName || !form.region || !form.baseCurrency || !form.timezone }
          >
            { `Create` }
          </Button>
        </div>
      </div>
    </Modal>
  );
}
